import React, { useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    Divider,
    Typography
} from "@mui/material";
import { Link as RouterLink, Navigate, useParams, useSearchParams } from "react-router-dom";
import { SURFACE, TEXT } from "../../dark-theme/palette";
import { resolveAppSiteEntry, APP_SITE_ID } from "../../config/appSiteLinks";
import { usePagesTranslation, normalizeLangParam } from "../locales";
import { trackEvent } from "../../services/GoogleAnalytics";
import { hasWebPremiumSupportAccess } from "../../services/PayPlanUtil";
import UserService from "../../services/UserService";
import Logger from "../../services/Logger";
import FooterContactUsCard from "./FooterContactUsCard";

const linkSx = {
    color: "#3BA666",
    fontWeight: 700,
    textDecoration: "underline",
    '&:hover': { textDecoration: 'none' }
};

const SupportMain = () => {
    const { appId } = useParams();
    const appEntry = resolveAppSiteEntry(appId);
    const [searchParams] = useSearchParams();
    const urlLang = normalizeLangParam(searchParams.get("lang"));
    const { t } = usePagesTranslation(urlLang);
    const [supportGate, setSupportGate] = useState("loading");
    useEffect(() => {
        trackEvent('entered', { name: 'SupportMain' });
    }, []);

    const isWebSupport = appEntry?.id === APP_SITE_ID.WEB;
    useEffect(() => {
        if (!isWebSupport) {
            setSupportGate("open");
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const userInfo = await UserService.getUserInfo();
                if (cancelled) return;
                setSupportGate(hasWebPremiumSupportAccess(userInfo?.cloudUser) ? "open" : "locked");
            } catch (e) {
                Logger.error("Support access check:", e);
                if (!cancelled) setSupportGate("locked");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isWebSupport]);

    const faqs = useMemo(() => [
        {
            question: t("support.faq0q"),
            answer: (
                <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq0a")}</Typography>
                </Box>
            )
        },
        {
            question: t("support.faq1q"),
            answer: (
                <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq1a_p1")}</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq1a_p2")}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{t("support.faq1a_note")}</Typography>
                </Box>
            )
        },
        {
            question: t("support.faq2q"),
            answer: (
                <Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>{t("support.faq2a_anonymous")}</Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq2a_anon1")}</Typography>
                        <Typography component="li" variant="body1">{t("support.faq2a_anon2")}</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>{t("support.faq2a_free")}</Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq2a_free1")}</Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq2a_free2")}</Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq2a_free3")}</Typography>
                        <Typography component="li" variant="body1">{t("support.faq2a_free4")}</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>{t("support.faq2a_standard")}</Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq2a_prem1")}</Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq2a_prem2")}</Typography>
                        <Typography component="li" variant="body1">{t("support.faq2a_prem3")}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 2 }}>{t("support.faq2a_note")}</Typography>
                </Box>
            )
        },
        {
            question: t("support.faq3q"),
            answer: (
                <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq3a_intro")}</Typography>
                    <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq3a_s1")}</Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq3a_s2")}</Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq3a_s3")}</Typography>
                        <Typography component="li" variant="body1">{t("support.faq3a_s4")}</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>{t("support.faq3a_when")}</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq3a_when_p")}</Typography>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>{t("support.faq3a_data")}</Typography>
                    <Typography variant="body1">{t("support.faq3a_data_p")}</Typography>
                </Box>
            )
        },
        {
            question: t("support.faq4q"),
            answer: (
                <Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>{t("support.faq4a_title")}</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq4a_p1")}</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq4a_p2")}</Typography>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>{t("support.faq4a_why")}</Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq4a_why1")}</Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1 }}>{t("support.faq4a_why2")}</Typography>
                        <Typography component="li" variant="body1">{t("support.faq4a_why3")}</Typography>
                    </Box>
                </Box>
            )
        },
        {
            question: t("support.faq5q"),
            answer: (
                <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq5a_intro")}</Typography>
                    <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                        <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                            <Typography component="span" sx={{ fontWeight: 700 }}>{t("support.faq5a_s1_label")}</Typography> {t("support.faq5a_s1")}
                        </Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                            <Typography component="span" sx={{ fontWeight: 700 }}>{t("support.faq5a_s2_label")}</Typography> {t("support.faq5a_s2")}
                        </Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                            <Typography component="span" sx={{ fontWeight: 700 }}>{t("support.faq5a_s3_label")}</Typography> {t("support.faq5a_s3")}
                        </Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                            <Typography component="span" sx={{ fontWeight: 700 }}>{t("support.faq5a_s4_label")}</Typography> {t("support.faq5a_s4")}
                        </Typography>
                        <Typography component="li" variant="body1">
                            <Typography component="span" sx={{ fontWeight: 700 }}>{t("support.faq5a_s5_label")}</Typography> {t("support.faq5a_s5")}
                        </Typography>
                    </Box>
                </Box>
            )
        },
        {
            question: t("support.faq6q"),
            answer: (
                <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq6a_p1")}</Typography>
                    <Typography variant="body1">{t("support.faq6a_p2")}</Typography>
                </Box>
            )
        },
        {
            question: t("support.faq7q"),
            answer: (
                <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t("support.faq7a_p1_prefix")}
                        <Box component="a" href="https://en.wiktionary.org" target="_blank" rel="noopener noreferrer" sx={linkSx}>
                            {t("support.faq7a_p1_link")}
                        </Box>
                        {t("support.faq7a_p1_suffix")}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>{t("support.faq7a_p2")}</Typography>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>{t("support.faq7a_license")}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>{t("support.faq7a_license_note")}</Typography>
                    <Typography variant="body1">
                        {t("support.faq7a_cc_prefix")}
                        <Box component="a" href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noopener noreferrer" sx={linkSx}>
                            {t("support.faq7a_cc_link")}
                        </Box>
                        {t("support.faq7a_cc_suffix")}
                    </Typography>
                </Box>
            )
        }
    ], [t]);

    if (!appEntry) {
        return <Navigate to={`/support/${APP_SITE_ID.WEB}`} replace />;
    }

    const pageTitle = appEntry.id === APP_SITE_ID.WEB
        ? t("support.title")
        : `${t("support.title")} — ${appEntry.displayName}`;

    const pageIntro = appEntry.id === APP_SITE_ID.WEB
        ? null
        : t("support.appIntro", { appName: appEntry.displayName });

    return (
        <Box sx={{
            width: "100%",
            minHeight: "100vh",
            py: 4
        }}>
            <Box sx={{
                maxWidth: "900px",
                mx: "auto",
                px: 3
            }}>
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                        textAlign: "center",
                        mb: pageIntro ? 1.5 : 4,
                        fontWeight: 700,
                        color: "#3BA666"
                    }}
                >
                    {pageTitle}
                </Typography>

                {pageIntro && (
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: "center",
                            mb: 4,
                            color: TEXT.secondary,
                            maxWidth: 640,
                            mx: "auto",
                        }}
                    >
                        {pageIntro}
                    </Typography>
                )}

                {supportGate === "loading" ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : null}

                {supportGate === "locked" ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: TEXT.primary }}>
                            {t("dashboard.support-locked-title")}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: TEXT.secondary, maxWidth: 560, mx: "auto" }}>
                            {t("dashboard.support-locked-body")}
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/subscription/plans"
                            variant="contained"
                            sx={{ textTransform: "none", minHeight: 44 }}
                        >
                            {t("dashboard.support-locked-cta")}
                        </Button>
                    </Box>
                ) : null}

                {supportGate === "open" ? (
                <>
                {/* FAQ Section */}
                <Typography
                    variant="h5"
                    sx={{
                        mb: 3,
                        fontWeight: 600,
                        color: TEXT.primary
                    }}
                >
                    {t("support.faqTitle")}
                </Typography>

                <Box sx={{ mb: 4 }}>
                    {faqs.map((faq, index) => (
                        <Accordion
                            key={index}
                            sx={{
                                mb: 2,
                                boxShadow: 2,
                                '&:before': {
                                    display: 'none'
                                },
                                '&.Mui-expanded': {
                                    margin: '16px 0'
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: "#3BA666" }} />}
                                sx={{
                                    backgroundColor: SURFACE.card,
                                    '&:hover': {
                                        backgroundColor: SURFACE.hover
                                    },
                                    '&.Mui-expanded': {
                                        backgroundColor: "rgba(59, 166, 102, 0.16)",
                                        borderBottom: '1px solid #3BA666'
                                    }
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        color: TEXT.primary
                                    }}
                                >
                                    {faq.question}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{
                                    backgroundColor: SURFACE.card,
                                    pt: 3,
                                    pb: 3
                                }}
                            >
                                {faq.answer}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                <Divider sx={{ my: 4 }} />

                <FooterContactUsCard t={t} appId={appEntry?.id} />
                </>
                ) : null}
            </Box>
        </Box>
    );
};

export default SupportMain;
